import React, {lazy, Suspense, useEffect, useMemo, useState} from 'react';
import Topbar from "../layout/Topbar";
import Sidebar from "../layout/Sidebar";
import Stats from "./Stats";
import OverviewTable from "./OverviewTable";
import PerformanceChart from "./PerformanceChart";
import useDashboardReport from "./useDashboardReport";
import {connect} from "react-redux";
import {dashboardActions} from "../../store/actions/dashboardActions";
import {Refresh} from "@mui/icons-material";
import {Box, CssBaseline, IconButton, Tooltip, Typography, useMediaQuery, useTheme} from "@mui/material";
import CustomSnackbar from "../alert/CustomSnackbar";
import AirbnbDateRangePicker from "../utils/AirbnbDateRangePicker";
import {workspaceActions} from "../../store/actions/workspaceAction";
import {styled} from "@mui/system";
import {alertActions} from "../../store/actions/alertActions";
import useDashboardPreference from './useDashboardPreference';

const WorldMap = lazy(() => import('./WorldMap'));

export const CustomMainRootBox = styled(Box)(({theme}) => ({
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    maxWidth: '100%',
}));

export const CustomDataGridBox = styled(Box)(({theme}) => ({
    height: "calc(var(--app-height) - 150px)",
    width: "100%",
    [theme.breakpoints.down(1469)]: {
        height: "calc(var(--app-height) - 158px)",
    }
}));

export const CustomMainContainerBox = styled(Box)(({theme}) => ({
    height: "var(--app-height)",
    width: "100%",
    overflow: "auto",
    borderLeft: "1px solid " + theme.palette.background.tableBorder,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingTop: "58px",
    [theme.breakpoints.down(600)]: {
        paddingTop: "58px",
    }
}));

export function Dashboard(props) {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [campaignMetric, setCampaignMetric] = useDashboardPreference('campaignMetric', 'revenue');
    const [offerMetric, setOfferMetric] = useDashboardPreference('offerMetric', 'revenue');
    const [refreshVersion, setRefreshVersion] = useState(0);
    const workspaceIds = useMemo(() => props.workspace.userWorkspaces === null ? null : props.workspace.userWorkspaces
        ?.filter(item => item.selected).map(item => item.workspaceId).join(',') || '', [props.workspace.userWorkspaces]);
    const {from, to, dateLabel} = props.dashboardState;
    const {timezone, currencyCode, loaded} = props.auth.userSettings;
    const filters = {from, to, timezone, currencyCode, workspace: workspaceIds, enabled: loaded, refreshVersion};
    const campaigns = useDashboardReport({...filters, groupBy: 'campaign_id', metric: campaignMetric});
    const offers = useDashboardReport({...filters, groupBy: 'offer_id', metric: offerMetric});
    const chart = useDashboardReport({...filters, groupBy: 'day'});
    const {updated} = props.workspace;
    const {resetUpdatedWorkspaceValue} = props;
    useEffect(() => {
        if (updated != null) {
            setRefreshVersion(value => value + 1);
            resetUpdatedWorkspaceValue();
        }
    }, [updated, resetUpdatedWorkspaceValue]);

    return (
        <CustomMainRootBox>
            <CssBaseline/>
            <Topbar/>
            <Sidebar {...props}/>
            <CustomMainContainerBox sx={{backgroundColor: 'background.default', px: {xs: 2, lg: 3}, pb: 3}}>
                {props?.alert?.errorMsg && <CustomSnackbar message={props.alert.errorMsg}/>}
                <Box sx={{maxWidth: 1800, mx: 'auto', pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5}}>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
                        <Box sx={{display: {xs: 'none', sm: 'block'}}}>
                            <Typography component="h1" sx={{fontSize: {xs: 24, sm: 28}, fontWeight: 700, letterSpacing: '-0.6px'}}>Dashboard</Typography>
                            <Typography sx={{mt: 0.5, fontSize: 13, color: 'text.secondary'}}>An overview of your campaign performance</Typography>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, ml: 'auto', maxWidth: '100%'}}>
                            <AirbnbDateRangePicker from={from} to={to} dateLabel={dateLabel}
                                compact={mobile}
                                triggerSx={mobile ? {bgcolor: 'background.darkBlue'} : []}
                                onChangeDate={(from, to, dateLabel) => props.updateDateRange(from, to, dateLabel)}/>
                            <Tooltip title="Refresh dashboard">
                                <IconButton color="inherit" onClick={() => setRefreshVersion(value => value + 1)} aria-label="Refresh dashboard"
                                    sx={{border: 1, borderColor: 'divider', borderRadius: '8px', backgroundColor: 'background.darkBlue'}}>
                                    <Refresh fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Stats stats={campaigns.data?.total} currencyCode={currencyCode} error={campaigns.error}/>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))'}, gap: 2.5, alignItems: 'stretch'}}>
                        <PerformanceChart data={chart.data} currencyCode={currencyCode} error={chart.error}/>
                        <Suspense fallback={<Box role="status" sx={{p: 2, minHeight: 450}}>Loading world map…</Box>}>
                            <WorldMap {...filters}/>
                        </Suspense>
                    </Box>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))'}, gap: 2.5}}>
                        <OverviewTable data={campaigns.data?.records} title="Top Campaigns" metric={campaignMetric}
                            onMetricChange={setCampaignMetric} currencyCode={currencyCode} error={campaigns.error}/>
                        <OverviewTable data={offers.data?.records} title="Top Offers" metric={offerMetric}
                            onMetricChange={setOfferMetric} currencyCode={currencyCode} error={offers.error}/>
                    </Box>
                </Box>
            </CustomMainContainerBox>
        </CustomMainRootBox>
    );
}

const mapStateToProps = (state) => ({
    dashboardState: state.dashboard,
    auth: state.auth,
    alert: state.alert,
    workspace: state.workspace
});

const mapDispatchToProps = {
    updateDateRange: dashboardActions.updateDateRange,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue,
    errorMessage: alertActions.error
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
